// @flow
import React from 'react';
import {
  Switch,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  Checkbox,
  Grid,
  MultiSelect,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { DatePicker } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { FormattedMessage } from 'react-intl';
import { TextInput } from '../../BasicDetails/TextInput';
import { displayAsDashesIfEmpty, stripOnlyWhiteSpaceToNull } from '../utils';
import { disableAllDaysOfTheMonthExceptFirst } from '../../../../utils/date-helpers';
import integrationSetupMessages from '../../IntegrationsSetup/messages';
import moment from 'moment';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';

// htmlFor turns the text into a <label> tied to the input, so screen readers announce it.
export const SetupLabel = ({ htmlFor, ...props }) => {
  return (
    <Typography
      variant={'labelLarge'}
      display={'block'}
      mb={1}
      {...(htmlFor && { component: 'label', htmlFor })}
    >
      <FormattedMessage {...props} />
    </Typography>
  );
};

export const SetupValue = ({
  editMode,
  id,
  name,
  value,
  onChange,
  placeholder,
  editable = true,
  valueMapper,
  maxLength,
  minLength,
  required = false,
  type,
  validateFunction,
  message,
}) => {
  return (
    <Grid item xs={7} sx={{ padding: '0 5px' }}>
      {editMode && editable ? (
        <TextInput
          id={id}
          name={name}
          value={value}
          editMode={editMode}
          placeholder={placeholder}
          onChange={({ target: { name, value } }) => {
            onChange({
              target: {
                name,
                value: stripOnlyWhiteSpaceToNull(value),
              },
            });
          }}
          maxLength={maxLength ? maxLength : '255'}
          minLength={required ? '1' : minLength ?? '0'}
          required={required}
          type={type}
          validateFunction={validateFunction}
          message={message}
        />
      ) : (
        <Typography variant={'bodyMedium'}>
          {valueMapper
            ? displayAsDashesIfEmpty(valueMapper[value])
            : displayAsDashesIfEmpty(value)}
        </Typography>
      )}
    </Grid>
  );
};

export const SetupSwitch = ({
  editMode,
  disabled,
  name,
  value,
  onChange,
  switchId,
  trueValue = (
    <Typography variant={'bodyMedium'} color={'primary.main'}>
      <FormattedMessage {...integrationSetupMessages.on} />
    </Typography>
  ),
  falseValue = (
    <Typography variant={'bodyMedium'} color={'text.secondary'}>
      <FormattedMessage {...integrationSetupMessages.off} />
    </Typography>
  ),
}) => {
  return (
    <Grid item xs={7} sx={{ padding: '0 5px' }}>
      {editMode ? (
        <Switch
          id={switchId}
          name={name}
          checked={value}
          disabled={disabled}
          onChange={(evt) => {
            const name = evt.target.name;
            const value = evt.target.checked;
            onChange({ target: { name, value } });
          }}
        />
      ) : value ? (
        trueValue
      ) : (
        falseValue
      )}
    </Grid>
  );
};

export const SetupCheckbox = ({
  editMode,
  disabled,
  name,
  value,
  onChange,
  label,
  checkboxId,
}) => {
  return (
    <Grid item xs={9} sx={{ padding: '0 5px' }}>
      <Checkbox
        id={checkboxId}
        name={name}
        label={label}
        checked={!!value}
        disabled={disabled || !editMode}
        onChange={() => {
          if (!editMode) return;
          onChange({ target: { name, value: !value } });
        }}
      />
    </Grid>
  );
};

export const SetupTitle = ({ children, TooltipProps, GridProps }) => {
  return (
    <Grid item xs={12} {...GridProps}>
      <Typography variant={'titleMedium'} component="div">
        {children}
      </Typography>
      {TooltipProps ? (
        <Tooltip variant="light" {...TooltipProps}>
          <AlertInfoIcon />
        </Tooltip>
      ) : null}
    </Grid>
  );
};

export const AnnualRecertMonthPicker = ({
  handleAnnualRecertMonthChange,
  editMode,
  programName,
  value,
}) => {
  const isNumberOfMonth = Number.isInteger(value) && value > 0;
  return (
    <DatePicker
      id={`${programName?.toLowerCase()}-annual-recert-month-picker`}
      views={['month']}
      format={'MMMM'}
      placeholder={'Select a month'}
      onChange={handleAnnualRecertMonthChange}
      disabled={!editMode}
      value={
        isNumberOfMonth
          ? moment().set('month', value - 1)
          : value
          ? moment(value)
          : null
      }
      shouldDisableDate={(date) => disableAllDaysOfTheMonthExceptFirst(date)}
      rules={{
        validate: (date) => {
          let isValid = false;
          if (date) {
            const dayOfMonth = new Date(date).getDate();
            isValid = dayOfMonth === 1;
          }
          if (!isValid) {
            return 'Date must be the first day of the month';
          }
          return isValid;
        },
      }}
    />
  );
};

export const SetupMultiSelect = ({ editMode = true, ...props }) => {
  const { value, changeFunction, name } = props;

  const handleChange = (newValue) => {
    changeFunction({
      target: { name, value: newValue },
    });
  };

  const selectedOptions = Array.isArray(value)
    ? value.map((el) => (typeof el === 'string' ? el : el.value || el.id))
    : [];

  return (
    <MultiSelect
      readOnly={!editMode}
      {...props}
      onChange={handleChange}
      value={selectedOptions}
    />
  );
};

export const SetupDatePicker = ({
  editMode,
  label,
  name,
  value,
  onChange,
  id,
  error = false,
  helperText = null,
}) => {
  const handleDateChange = (date) => {
    const formattedDate = date ? formatDateDB(date) : null;
    onChange({
      target: { name, value: formattedDate },
    });
  };

  const dateValue = value ? moment(value) : null;

  return (
    <Grid item xs={7}>
      <DatePicker
        id={id}
        label={label}
        onChange={handleDateChange}
        value={dateValue}
        error={error}
        helperText={helperText}
        readOnly={!editMode}
      />
    </Grid>
  );
};
